import React from 'react';
import { cn } from '@/lib/utils';

// Generic Skeleton Component
export const Skeleton = ({ className, ...props }) => (
  <div
    className={cn('bg-muted shimmer rounded-md', className)}
    {...props}
  />
);

// Hero Skeleton
export const HeroSkeleton = () => (
  <div className="relative w-full h-[85vh] md:h-[90vh] bg-muted">
    {/* Background shimmer */}
    <div className="absolute inset-0 shimmer" />
    
    {/* Gradient overlay */}
    <div className="absolute inset-0 hero-gradient-bottom" />
    <div className="absolute inset-0 hero-gradient-side" />
    
    {/* Content skeleton */}
    <div className="absolute inset-0 flex items-center">
      <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-12">
        <div className="max-w-2xl lg:max-w-3xl space-y-6">
          {/* Badge */}
          <Skeleton className="h-6 w-32" />
          
          {/* Title */}
          <Skeleton className="h-16 md:h-24 w-full max-w-xl" />
          
          {/* Tagline */}
          <Skeleton className="h-8 w-3/4" />
          
          {/* Description */}
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-4/6" />
          </div>
          
          {/* Genres */}
          <div className="flex gap-2">
            <Skeleton className="h-8 w-20 rounded-full" />
            <Skeleton className="h-8 w-24 rounded-full" />
            <Skeleton className="h-8 w-16 rounded-full" />
          </div>
          
          {/* Buttons */}
          <div className="flex gap-4">
            <Skeleton className="h-14 w-40 rounded-lg" />
            <Skeleton className="h-14 w-36 rounded-lg" />
          </div>
        </div>
      </div>
    </div>
    
    {/* Indicators */}
    <div className="absolute bottom-8 md:bottom-12 right-4 sm:right-6 lg:right-12 flex items-center gap-4">
      <Skeleton className="w-10 h-10 rounded-full" />
      <div className="flex gap-2">
        <Skeleton className="w-8 h-1 rounded-full" />
        <Skeleton className="w-4 h-1 rounded-full" />
        <Skeleton className="w-4 h-1 rounded-full" />
      </div>
    </div>
  </div>
);

// Movie Card Skeleton
export const MovieCardSkeleton = () => (
  <div className="flex-shrink-0 w-[140px] sm:w-[160px] md:w-[180px] lg:w-[200px]">
    <Skeleton className="aspect-poster rounded-lg" />
    <div className="mt-2 space-y-1">
      <Skeleton className="h-4 w-4/5" />
      <Skeleton className="h-3 w-1/3" />
    </div>
  </div>
);

// Carousel Skeleton
export const CarouselSkeleton = ({ count = 7 }) => (
  <div className="py-4 md:py-6">
    {/* Title */}
    <div className="px-4 sm:px-6 lg:px-12 mb-3 md:mb-4">
      <Skeleton className="h-7 w-40" />
    </div>
    
    {/* Cards */}
    <div className="flex gap-3 md:gap-4 px-4 sm:px-6 lg:px-12 overflow-hidden">
      {Array.from({ length: count }).map((_, i) => (
        <MovieCardSkeleton key={i} />
      ))}
    </div>
  </div>
);

// Page Loading Skeleton
export const PageSkeleton = () => (
  <div className="min-h-screen bg-background">
    <HeroSkeleton />
    <div className="space-y-2 pb-12">
      <CarouselSkeleton />
      <CarouselSkeleton />
      <CarouselSkeleton />
    </div>
  </div>
);

// Grid Skeleton for Movies Page
export const MoviesGridSkeleton = ({ count = 12 }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6">
    {Array.from({ length: count }).map((_, i) => (
      <MovieCardSkeleton key={i} />
    ))}
  </div>
);

export default Skeleton;
